import type { CSSProperties, ReactNode } from 'react';
import { useFormat } from '@paige/format';
import { FlowGlyph, FlowSpark } from './FlowGlyph';
import { FlowChips, FlowNow } from './FlowStage';
import { runIsQuiet, type FlowStatus } from './flow-state';
import { placeName, runColumn, runKind, unitWord } from './flow-words';
import type { FlowCell, FlowPlace, FlowRun } from './flow-types';

/** The spine's columns the lane runs under, in order. */
const COLUMNS: FlowPlace[] = ['preparing', 'reading', 'splitting', 'understanding', 'checking'];
const MAX_PER_COLUMN = 2;

/** One reading: a ring filled as far as it has got, the count, and whose it is. */
function RunMark({ run }: { run: FlowRun }): ReactNode {
  const f = useFormat();
  const share = run.total > 0 ? Math.min(1, run.completed / run.total) : 0;
  const quiet = runIsQuiet(run);
  return (
    <li
      className={quiet ? 'fl-run fl-run--quiet' : 'fl-run'}
      data-run={run.runId}
      title={`${runKind(run)} for ${run.companyName}, ${run.jobName}: running ${f.duration(run.runningSeconds)}`}
    >
      <svg className="fl-run__ring" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
        <circle cx="12" cy="12" r="9" className="fl-run__rail" />
        <circle
          cx="12"
          cy="12"
          r="9"
          className="fl-run__arc"
          pathLength={100}
          style={{ strokeDasharray: `${(share * 100).toFixed(1)} 100` } as CSSProperties}
        />
      </svg>
      <span className="fl-run__text">
        <strong>
          {run.total > 0 ? `${f.int(run.completed)} of ${f.int(run.total)}` : runKind(run)}
        </strong>
        <span className="fl-run__who">
          {quiet ? `no progress ${f.duration(run.quietSeconds)}` : run.companyName}
        </span>
      </span>
    </li>
  );
}

/** The readings sitting under one spine column; past two, the rest are counted. */
function RunColumn({ place, runs }: { place: FlowPlace; runs: FlowRun[] }): ReactNode {
  const here = runs.filter((r) => runColumn(r) === place);
  return (
    <ul
      className="fl-engine__stop"
      data-under={place}
      aria-label={`In the engine, ${placeName[place]}`}
    >
      {here.slice(0, MAX_PER_COLUMN).map((r) => (
        <RunMark key={r.runId} run={r} />
      ))}
      {here.length > MAX_PER_COLUMN ? (
        <li className="fl-run fl-run--more">+{here.length - MAX_PER_COLUMN} more</li>
      ) : null}
    </ul>
  );
}

/** The lane's own line: what waits to be handed to an engine, what is being read, and why anything stands. */
function EngineHead({
  cell,
  status,
  here,
  selected,
}: {
  cell: FlowCell;
  status: FlowStatus;
  here: number;
  selected: boolean;
}): ReactNode {
  const f = useFormat();
  return (
    <button type="button" className="fl-engine__head" aria-pressed={selected}>
      <span className="fl-stage__head">
        <FlowGlyph tone={status.tone} />
        <span className="fl-stage__name">Read by the job&rsquo;s engine</span>
      </span>
      <span className="fl-engine__facts">
        <span>
          <strong className="fl-engine__num">{f.int(here)}</strong> {unitWord(cell.unit, here)} here
        </span>
        <FlowNow cell={cell} f={f} />
      </span>
      <FlowChips notes={status.notes} />
    </button>
  );
}

/**
 * The engine road between arriving and review as one lane: a job's engine reads a capture in one run and has
 * no stage units, so the lane spans the spine's five stages and each running reading sits under the stage the
 * engine says it is at, with how far it has got. What waits to be handed to an engine is counted on the left.
 */
export function FlowEngineLane({
  cell,
  runs,
  status,
  selected,
  onSelect,
  halt = null,
  lit = false,
}: {
  cell: FlowCell;
  runs: FlowRun[];
  status: FlowStatus;
  selected: boolean;
  onSelect: () => void;
  /** Where work stopped in the lane, when it did. */
  halt?: ReactNode;
  /** The cards strip points here. */
  lit?: boolean;
}): ReactNode {
  const classes = [
    'fl-engine',
    `fl-stage--${status.tone}`,
    selected && 'fl-stage--open',
    lit && 'fl-stage--lit',
  ]
    .filter(Boolean)
    .join(' ');
  const working = Math.max(runs.length, cell.working);
  const here = status.here - cell.working + working;
  return (
    <div className={classes} data-place="engine" data-tone={status.tone} onClick={onSelect}>
      {halt}
      <EngineHead cell={{ ...cell, working }} status={status} here={here} selected={selected} />
      <div className="fl-engine__rail">
        {COLUMNS.map((place) => (
          <RunColumn key={place} place={place} runs={runs} />
        ))}
      </div>
      <FlowSpark values={cell.series} label="In the engine: pages read over 15 minutes" />
    </div>
  );
}
